"use client";

/**
 * What the films draw with: BB's release covers and the people on BB (loaded
 * once), and the ready-made strings the drawn tools show — prices in the
 * viewer's currency, chat lines in their language. Shared by /screens and the
 * role films on /why-bb/story.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { getVisibleReleases } from "@/services/home";
import { niceStep, snap, useDisplayCurrency } from "@/components/currency/display-currency";
import type { LinesFor } from "./films";

function shuffled<T>(list: T[]): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function useBbArt() {
  const [covers, setCovers] = useState<string[]>([]);
  const [people, setPeople] = useState<string[]>([]);
  useEffect(() => {
    let alive = true;
    getVisibleReleases().then(list => { if (alive) setCovers(shuffled(list.map(r => r.imageUrl).filter(Boolean)).slice(0, 40)); }).catch(() => {});
    fetch("/api/launch/people").then(r => r.json()).then((d: { founders?: string[]; people?: string[] }) => {
      if (alive) setPeople(shuffled([...(d.founders || []), ...(d.people || [])]).slice(0, 24));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return { covers, people };
}

const SHOTS = {
  release: "/images/hero-tips/release.jpg",
  news: "/images/hero-tips/news.jpg",
  market: "/images/hero-tips/marketplace.jpg",
  sync: "/images/hero-tips/sync.jpg",
};

export function useFilmLines() {
  const t = useTranslations("screens");
  const locale = useLocale();
  const cur = useDisplayCurrency();
  return useCallback((k: LinesFor): string[] => {
    const fmt = (sek: number) => {
      const v = snap(sek * cur.perSek, niceStep(sek * cur.perSek, 10));
      return new Intl.NumberFormat(locale, { style: "currency", currency: cur.code, maximumFractionDigits: 0 }).format(v);
    };
    switch (k) {
      case "prices": return [fmt(50), fmt(100), fmt(150)];
      case "ticket": return [fmt(150)];
      case "meter": return ["−14 LUFS", t("master.meterTarget")];
      case "chat": return [t("copi.c1"), t("copi.c2"), t("copi.c3"), t("copi.c4")];
      case "shotsRelease": return [SHOTS.release, SHOTS.news, SHOTS.market, SHOTS.sync];
      case "shotsMarket": return [SHOTS.market, SHOTS.release, SHOTS.sync, SHOTS.news];
      case "shotsSync": return [SHOTS.sync, SHOTS.release, SHOTS.market, SHOTS.news];
      case "surveyUrl": return ["https://www.breakinbread.com/survey?e=pro-launch"];
      case "joinUrl": return ["https://www.breakinbread.com/signin?mode=signup"];
      case "ledger": return [`+${fmt(150)}`, `−${fmt(40)}`, `+${fmt(100)}`, `−${fmt(25)}`];
    }
  }, [cur.perSek, cur.code, locale, t]);
}

/**
 * Real BB tracks measured once with ffmpeg (Gordon 2026-11-03: "all audio
 * graphics must be authentic"): waveform peaks of a 30 s excerpt (16 per
 * second), EBU R128 loudness and the momentary loudness every 0.4 s.
 * Re-measure: node --import tsx scripts/film-audio/measure.ts <tmp dir> public/data/film-audio.json
 */
export interface FilmAudioTrack {
  id: string;
  title: string;
  artist: string;
  cover: string;
  bpm: number | null;
  key: string | null;
  duration: number;
  lufs: number | null;
  truePeak: number | null;
  lra: number | null;
  overview: number[];
  excerptFrom: number;
  excerpt: number[];
  momentary: number[];
}

let audioOnce: Promise<FilmAudioTrack[]> | null = null;
export function useFilmAudio(): FilmAudioTrack[] {
  const [tracks, setTracks] = useState<FilmAudioTrack[]>([]);
  useEffect(() => {
    audioOnce ||= fetch("/data/film-audio.json").then(r => r.json()).then(d => (Array.isArray(d?.tracks) ? d.tracks : [])).catch(() => []);
    let alive = true;
    void audioOnce.then(t => { if (alive) setTracks(t); });
    return () => { alive = false; };
  }, []);
  return tracks;
}
